import { useState, useEffect, useRef, useCallback, Component } from 'react';
import { api, API_BASE } from '../../utils/api';
import { useVoicePaging, isActivateKey } from '../../hooks/useVoicePaging';
import { nowEatTimeString } from '../../utils/datetime';

const DISPLAY_POLL_MS = 1000;

class DisplayErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          width: '100vw', height: '100vh', background: '#0a1628',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 40, textAlign: 'center', fontFamily: 'sans-serif',
        }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Display failed to load</div>
            <div style={{ opacity: 0.7, marginBottom: 24, maxWidth: 520, lineHeight: 1.5 }}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: '#1565c0', color: '#fff', border: 'none',
                padding: '12px 28px', borderRadius: 8, fontSize: 16, cursor: 'pointer',
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
function MediaPlayer({ playlist, muted }) {
  const [idx, setIdx] = useState(0);
  const videoRef = useRef();
  const timerRef = useRef();

  const advance = useCallback(() => {
    setIdx(i => (i + 1) % Math.max(playlist.length, 1));
  }, [playlist.length]);

  useEffect(() => {
    if (playlist.length === 0) return;
    clearTimeout(timerRef.current);
    const item = playlist[idx];
    if (item?.media_type === 'image') {
      timerRef.current = setTimeout(advance, (item.duration_sec || 8) * 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [idx, playlist, advance]);

  // Ensure video keeps playing on TVs where onEnded is unreliable
  useEffect(() => {
    const video = videoRef.current;
    const item = playlist[idx];
    if (!video || item?.media_type !== 'video') return undefined;

    const restart = () => {
      if (playlist.length === 1) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        advance();
      }
    };

    video.addEventListener('ended', restart);
    return () => video.removeEventListener('ended', restart);
  }, [idx, playlist, advance]);

  useEffect(() => {
    const video = videoRef.current;
    const item = playlist[idx];
    if (!video || item?.media_type !== 'video') return;
    video.load();
    video.play().catch(() => {});
  }, [idx, playlist]);

  if (playlist.length === 0) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>▷</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18 }}>No media in playlist</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Upload media via Admin → Media Manager</div>
        </div>
      </div>
    );
  }

  const item = playlist[idx];
  const src = `${API_BASE}${item.file_path}`;
  const loopSingleVideo = playlist.length === 1 && item?.media_type === 'video';

  return item?.media_type === 'video' ? (
    <video
      ref={videoRef}
      key={item.id}
      src={src}
      autoPlay
      loop={loopSingleVideo}
      muted={muted}
      playsInline
      onError={advance}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  ) : (
    <img key={item.id} src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', animation: 'fadeIn 0.5s ease' }} />
  );
}

function NowServing({ counters }) {
  const openCounters = counters.filter(c => c.is_open);
  return (
    <div className="display-now-serving" style={{
      background: 'var(--navy)', padding: '16px 32px', display: 'flex', alignItems: 'center',
      borderBottom: '4px solid var(--cobalt)', minHeight: 110, flexShrink: 0, gap: 24,
      overflowX: 'auto', scrollbarWidth: 'none', width: '100%'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 110, borderRight: '2px solid rgba(255,255,255,0.1)', paddingRight: 20 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-display)' }}>Now</span>
        <span style={{ fontSize: 20, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#fff', fontFamily: 'var(--font-display)', marginTop: -2 }}>Serving</span>
      </div>
      <div style={{ display: 'flex', flex: 1, gap: 20, width: '100%' }}>
        {openCounters.length === 0 ? (
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
            No active counters open
          </div>
        ) : (
          openCounters.map(c => (
            <div key={c.counter_id} className="display-counter-card" style={{
              flex: 1,
              background: 'rgba(255,255,255,0.03)',
              border: c.serving_ticket ? '1.5px solid rgba(66,165,245,0.4)' : '1.5px solid rgba(255,255,255,0.08)',
              borderRadius: 14,
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minWidth: 180,
              boxShadow: c.serving_ticket ? '0 0 20px rgba(66,165,245,0.15)' : 'none',
              borderLeft: c.serving_ticket ? '5px solid var(--cobalt-bright)' : '1.5px solid rgba(255,255,255,0.08)',
              transition: 'var(--transition)'
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-display)' }}>
                  {c.counter_label || c.counter_name}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 2, fontWeight: 500 }}>
                  {c.counter_name}
                </div>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 32, fontWeight: 800, color: c.serving_ticket ? '#fff' : 'rgba(255,255,255,0.15)', textShadow: c.serving_ticket ? '0 0 12px rgba(66,165,245,0.6)' : 'none' }}>
                {c.serving_ticket || '—'}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function QueueStatusGrid({ counters }) {
  if (counters.length === 0) return null;

  return (
    <div style={{
      background: 'var(--navy-mid)',
      borderTop: '2px solid rgba(255,255,255,0.08)',
      flexShrink: 0,
      minHeight: 120,
      display: 'flex',
      width: '100%',
      overflowX: 'auto',
      scrollbarWidth: 'none',
    }}>
      {counters.map((c, i) => (
        <div key={c.counter_id} className="display-queue-cell" style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRight: i < counters.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
          padding: '16px 24px',
          minWidth: 200,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {c.counter_name}
            </span>
            <span style={{ fontSize: 11, color: c.is_open ? 'var(--green)' : 'var(--red)', fontWeight: 800 }}>
              {c.is_open ? '🟢' : '🔴'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'hidden' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', fontFamily: 'var(--font-display)' }}>Next:</span>
            {c.is_open ? (
              c.next_tickets && c.next_tickets.length > 0 ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {c.next_tickets.map((t, idx) => (
                    <span key={t} style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: idx === 0 ? 20 : 16,
                      fontWeight: idx === 0 ? 800 : 600,
                      color: idx === 0 ? 'var(--cobalt-bright)' : 'rgba(255,255,255,0.5)',
                      background: idx === 0 ? 'rgba(66,165,245,0.12)' : 'rgba(255,255,255,0.03)',
                      padding: '4px 10px',
                      borderRadius: 8,
                      border: idx === 0 ? '1px solid rgba(66,165,245,0.3)' : '1px solid rgba(255,255,255,0.05)',
                    }}>
                      {t}
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>Queue empty</span>
              )
            ) : (
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Closed</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AudioActivationOverlay({ onActivate }) {
  const activate = (e) => {
    e?.preventDefault?.();
    onActivate();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => {
        if (isActivateKey(e)) activate(e);
      }}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 9999,
        background: 'rgba(10, 22, 40, 0.92)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <div style={{
        background: 'rgba(255,255,255,0.07)',
        border: '1.5px solid rgba(255,255,255,0.18)',
        borderRadius: 28, padding: '52px 64px',
        textAlign: 'center', maxWidth: 520,
        boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
      }}>
        <div style={{ fontSize: 72, marginBottom: 24 }}>🔊</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: '#fff', marginBottom: 12 }}>
          Enable Voice Paging
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16, lineHeight: 1.6, marginBottom: 32 }}>
          Ticket announcements play on this TV.<br />
          Press OK on the remote or tap to activate.
        </div>
        <div style={{
          background: 'linear-gradient(135deg, var(--cobalt), var(--cobalt-bright))',
          color: '#fff', fontFamily: 'var(--font-display)',
          fontWeight: 800, fontSize: 17,
          padding: '16px 40px', borderRadius: 50,
          display: 'inline-block',
        }}>
          Tap to Activate →
        </div>
      </div>
    </div>
  );
}

function LiveClock() {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
      {nowEatTimeString()}
    </div>
  );
}

function DisplayAppInner() {
  const [playlist, setPlaylist] = useState([]);
  const [counterStatus, setCounterStatus] = useState([{ counter_id: 'loading', counter_name: 'Connecting...', is_open: true, next_tickets: [] }]);
  const [config, setConfig] = useState({ display_media_muted: 'true' });
  const sinceRef = useRef(new Date().toISOString());
  const { audioUnlocked, unlockAudio, handleCallUpdate } = useVoicePaging({
    storageKey: 'qms_display_audio_unlocked',
    enabled: true,
  });

  const activateAudio = useCallback(() => {
    unlockAudio();
    // Include calls that happened while the activation overlay was visible
    sinceRef.current = new Date(Date.now() - 10000).toISOString();
  }, [unlockAudio]);

  const refreshData = useCallback(() => {
    const since = sinceRef.current;
    const pollStarted = new Date().toISOString();
    api.get(`/api/display/status?since=${encodeURIComponent(since)}`)
      .then(res => {
        if (!res) return;
        sinceRef.current = pollStarted;
        if (res.counters) {
          setCounterStatus(res.counters);
        }
        handleCallUpdate(res.recent_calls || []);
      })
      .catch(err => console.error("Display status fetch failed:", err));
  }, [handleCallUpdate]);

  useEffect(() => {
    api.get('/api/display/playlist').then(setPlaylist).catch(e => console.error(e));
    api.get('/api/admin/config').then(setConfig).catch(e => console.error(e));
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    const pollInterval = setInterval(refreshData, DISPLAY_POLL_MS);
    return () => clearInterval(pollInterval);
  }, [refreshData]);

  useEffect(() => {
    if (!audioUnlocked) return;
    refreshData();
  }, [audioUnlocked, refreshData]);

  return (
    <div style={{ 
      width: '100vw', height: '100vh', maxHeight: '100vh', 
      background: '#000', display: 'flex', flexDirection: 'column', 
      overflow: 'hidden', position: 'relative' 
    }}>
      {!audioUnlocked && (
        <AudioActivationOverlay onActivate={activateAudio} />
      )}

      <NowServing counters={counterStatus} />
      
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--navy)' }}>
        <MediaPlayer playlist={playlist} muted={config.display_media_muted !== 'false'} />
        <div style={{ position: 'absolute', top: 20, right: 24, background: 'rgba(10,22,40,0.6)', backdropFilter: 'blur(8px)', padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'rgba(255,255,255,0.6)', letterSpacing: '-0.01em' }}>QMS Display</div>
          <LiveClock />
        </div>
      </div>

      <QueueStatusGrid counters={counterStatus} />
    </div>
  );
}

export default function DisplayApp() {
  return (
    <DisplayErrorBoundary>
      <DisplayAppInner />
    </DisplayErrorBoundary>
  );
}
